import { useMemo } from 'react';

import type { DataTableV2CellOrColumnActions } from './UserActions.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';

/**
 * Unified hook to get the builtin and custom actions for 'cell' or 'header'
 * contexts. FloatingMenu / ColumnActions / CellActions consume this one.
 */
export function useCellActions(props: {
  enabled: boolean;
  rowId: string;
  columnId: string;
  type: 'cell' | 'header';
}): DataTableV2CellOrColumnActions {
  const { enabled, columnId, rowId, type } = props;
  const table = useDataTableV2();

  // Find the right column and cell instance to provide
  const { customActions, builtinActions } =
    useMemo<DataTableV2CellOrColumnActions>(() => {
      if (enabled === false) {
        return { customActions: null, builtinActions: null };
      }

      const column = table.getColumn(columnId);

      if (column !== undefined && type === 'header') {
        return column.getUserActions();
      }

      if (rowId) {
        const rowCells = table.getRow(rowId).getAllCells();
        const columnCell = rowCells.find((cell) => cell.column.id === columnId);
        if (columnCell) {
          return columnCell?.getUserActions();
        }
      }

      return { customActions: null, builtinActions: null };
    }, [columnId, enabled, rowId, table, type]);

  return { customActions, builtinActions };
}
